import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react'
import PrimaryButton from '@organisms/PrimaryButton'
import Loader from '@atoms/Loader'
import BackButton from '@organisms/BackButton'
import { FinalTestQuestion } from '@models/statistic'
import NavigationBlock from '../NavigationBlock'
import ExpandButton from '../ScormViewer/ExpandButton'
import {
  DesktopExpandInModalWrapper,
  NavButtonsWrapper,
} from '../ScormViewer/styled'
import {
  AnswerStateIcon,
  ActionSlot,
  BottomControls,
  Card,
  ContentArea,
  Header,
  FeedbackBadge,
  FeedbackIcon,
  OptionInput,
  OptionRow,
  OptionShell,
  OptionsList,
  OptionText,
  PageBottomRow,
  PageTitle,
  QuestionText,
  Wrapper,
} from './styled'

type Props = {
  questions: FinalTestQuestion[]
  questionIndex: number
  onAnswer: (
    selectedOptionIds: (string | number)[],
    isSuccess: boolean
  ) => Promise<void>
  onContinue: () => Promise<void>
  handleBack: () => void
  inModal: boolean
  toggleInModal: Dispatch<SetStateAction<boolean>>
  prevRoute?: string | null
  nextRoute?: string | null
  onChangePage: () => Promise<void>
  isTestBlocked?: boolean
  onBlockedNavigation?: (pendingNavigation?: () => void) => void
  isPreviewMode?: boolean
}

const getOptionInputStatus = ({
  isAnswered,
  isSelected,
  isWrongSelected,
  showSuccessIcon,
}: {
  isAnswered: boolean
  isSelected: boolean
  isWrongSelected: boolean
  showSuccessIcon: boolean
}) => {
  if (isWrongSelected) {
    return 'wrong'
  }

  if (showSuccessIcon) {
    return 'success'
  }

  if (isAnswered) {
    return 'default'
  }

  return isSelected ? 'selected' : 'default'
}

const FinalTestViewer: FC<Props> = ({
  questions,
  questionIndex,
  onAnswer,
  onContinue,
  handleBack,
  inModal,
  toggleInModal,
  prevRoute,
  nextRoute,
  onChangePage,
  isTestBlocked,
  onBlockedNavigation,
  isPreviewMode,
}) => {
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([])
  const [submitResult, setSubmitResult] = useState<
    'idle' | 'success' | 'error'
  >('idle')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const question = questions[questionIndex]
  const totalQuestions = questions.length
  const questionNumber = questionIndex + 1

  const correctIds = useMemo(
    () =>
      question?.options
        .filter((option) => option.isCorrect)
        .map((option) => option.id) || [],
    [question?.options]
  )

  const isAnswered = submitResult !== 'idle'
  const isCorrectAnswer = submitResult === 'success'

  useEffect(() => {
    setSelectedIds([])
    setSubmitResult('idle')
    setIsSubmitting(false)
  }, [questionIndex])

  if (!question) {
    return <Loader isLoading />
  }

  const isSingle = question.type === 'single'

  const handleOptionToggle = (id: string | number) => {
    if (isAnswered || isSubmitting) return

    if (isSingle) {
      setSelectedIds([id])
    } else {
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      )
    }
  }

  const handleAnswer = () => {
    if (selectedIds.length === 0 || isSubmitting) return

    const isSuccess = isSingle
      ? correctIds.includes(selectedIds[0])
      : selectedIds.length === correctIds.length &&
        selectedIds.every((id) => correctIds.includes(id))

    setIsSubmitting(true)

    onAnswer(selectedIds, isSuccess)
      .then(() => {
        setSubmitResult(isSuccess ? 'success' : 'error')
      })
      .catch(() => {
        // Keep the answer editable if the attempt was not saved.
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const handleContinue = () => {
    if (isSubmitting) return

    setIsSubmitting(true)

    onContinue().finally(() => {
      setIsSubmitting(false)
    })
  }

  const handlePrimaryAction = () => {
    if (isSubmitting) return

    if (isAnswered) {
      handleContinue()

      return
    }

    handleAnswer()
  }

  return (
    <Wrapper $inModal={inModal}>
      <ContentArea $inModal={inModal}>
        <Header $inModal={inModal}>
          <BackButton onClick={handleBack} autoWidth>
            {isPreviewMode ? 'Назад' : 'Мое обучение'}
          </BackButton>
          <PageTitle>Итоговое тестирование</PageTitle>
        </Header>
        <Card $inModal={inModal}>
          <QuestionText>{question.text}</QuestionText>
          <OptionsList>
            {question.options.map((option) => {
              const isSelected = selectedIds.includes(option.id)
              const isWrongSelected = isAnswered && isSelected && !option.isCorrect
              const isCorrectSelected =
                isAnswered && isSelected && option.isCorrect
              const showCorrectAnswer =
                isAnswered && option.isCorrect && !isSelected
              const showSuccessIcon = isCorrectSelected || showCorrectAnswer
              const optionInputStatus = getOptionInputStatus({
                isAnswered,
                isSelected,
                isWrongSelected,
                showSuccessIcon,
              })

              return (
                <OptionShell key={option.id}>
                  <AnswerStateIcon
                    $isVisible={showSuccessIcon || isWrongSelected}
                    $isCorrect={showSuccessIcon}
                    aria-hidden="true"
                  >
                    {showSuccessIcon ? '✓' : '×'}
                  </AnswerStateIcon>
                  <OptionRow
                    $isAnswered={isAnswered}
                    $isSelected={isSelected && !isAnswered}
                    $isCorrectSelected={isCorrectSelected}
                    $isWrongSelected={isWrongSelected}
                    $isCorrectMissed={showCorrectAnswer}
                  >
                    <OptionInput
                      type={isSingle ? 'radio' : 'checkbox'}
                      checked={isSelected}
                      onChange={() => handleOptionToggle(option.id)}
                      name="final-test-option"
                      disabled={isAnswered || isSubmitting}
                      $status={optionInputStatus}
                    />
                    <OptionText>{option.text}</OptionText>
                  </OptionRow>
                </OptionShell>
              )
            })}
          </OptionsList>
          <PageBottomRow>
            <span>
              Вопрос: {questionNumber}/{totalQuestions}
            </span>
            {isAnswered ? (
              <FeedbackBadge $isSuccess={isCorrectAnswer}>
                <FeedbackIcon $isSuccess={isCorrectAnswer} aria-hidden="true">
                  {isCorrectAnswer ? '✓' : '×'}
                </FeedbackIcon>
                {isCorrectAnswer
                  ? 'Вы ответили правильно'
                  : 'Вы ответили неправильно'}
              </FeedbackBadge>
            ) : null}
            <ActionSlot>
              <PrimaryButton
                autoWidth
                disabled={isSubmitting || (selectedIds.length === 0 && !isAnswered)}
                onClick={handlePrimaryAction}
                withoutUpperCase
              >
                {isAnswered ? 'Продолжить' : 'Ответить'}
              </PrimaryButton>
            </ActionSlot>
          </PageBottomRow>
        </Card>
      </ContentArea>
      {inModal ? (
        <DesktopExpandInModalWrapper>
          <ExpandButton inModal={inModal} toggleInModal={toggleInModal} />
        </DesktopExpandInModalWrapper>
      ) : (
        <BottomControls>
          <BackButton onClick={handleBack}>
            {isPreviewMode ? 'Назад' : 'Весь курс'}
          </BackButton>
          <NavButtonsWrapper>
            <ExpandButton inModal={inModal} toggleInModal={toggleInModal} />
            <NavigationBlock
              prevRoute={prevRoute}
              nextRoute={nextRoute}
              onChangePage={onChangePage}
              isTestBlocked={isTestBlocked}
              onBlockedNavigation={onBlockedNavigation}
              isPreviewMode={isPreviewMode}
            />
          </NavButtonsWrapper>
        </BottomControls>
      )}
    </Wrapper>
  )
}

export default FinalTestViewer
